<template>
  <SectionFrame v-if="shown || inStudio" :section="section" :index="index" :heading="heading" label="Do we cover you?" :narrow="layout === 'inline'">
    <div v-if="shown" :class="layout === 'card' ? 't-card s2-cover-card' : ''" class="mo-reveal">
      <form class="s2-cover-form" @submit.prevent="check" role="search" :aria-label="heading || 'Check your postcode'">
        <label class="s2-label" :for="inputId">Your postcode</label>
        <div class="s2-cover-row">
          <input :id="inputId" v-model="postcode" class="s2-input" autocomplete="postal-code" inputmode="text" placeholder="e.g. TR1 2SJ" maxlength="10">
          <button type="submit" class="t-btn">Check</button>
        </div>
      </form>
      <div aria-live="polite" class="s2-cover-answer">
        <p v-if="answer === 'yes'" class="s2-cover-msg" data-answer="yes"><b>{{ section.yesText || "Yes, we cover you." }}</b>
          <a v-if="quoteLink" class="t-link s2-u" :href="back(quoteLink)">Get a quote</a></p>
        <p v-else-if="answer === 'near'" class="s2-cover-msg" data-answer="near"><b>{{ section.nearText || "Just outside our usual area." }}</b>
          <a class="t-link s2-u" :href="back('/contact')">Leave your number</a></p>
        <p v-else-if="answer === 'no'" class="s2-cover-msg" data-answer="no">{{ section.noText || "Sorry, we don't cover that area." }}</p>
        <p v-else-if="answer === 'unknown'" class="s2-err">That doesn't look like a postcode. Try one like TR1 2SJ.</p>
      </div>
      <p v-if="areas.note" class="s2-note">{{ areas.note }}</p>
    </div>
    <StudioGap v-else>Add the postcode areas you cover (Areas), then people can check theirs.</StudioGap>
  </SectionFrame>
</template>

<script setup>
import {computed, ref} from "vue";
import SectionFrame from "./SectionFrame.vue";
import StudioGap from "./StudioGap.vue";
import {coverCheck, layoutOf} from "@/utils/sectionLogic.js";
import {studioMode} from "@/classes/previewBridge.js";
import {useBackLinks} from "@/composables/useBackLinks.js";
const back = useBackLinks();

const props = defineProps({section: {type: Object, required: true}, index: {type: Number, default: 0}, data: {type: Object, default: () => ({})}});
const inStudio = computed(() => studioMode.value !== "off");
const layout = computed(() => layoutOf(props.section, ["inline", "card"]));
const heading = computed(() => props.section.heading ?? "Do we cover you?");
const areas = computed(() => props.data.serviceAreas || {});
const shown = computed(() => (areas.value.covered || []).length > 0);
const postcode = ref("");
const answer = ref("");
const inputId = computed(() => `pc-${String(props.section.id || "x").slice(0, 8)}`);
const quoteLink = computed(() => props.section.quoteLink || "/contact");
const check = () => { answer.value = coverCheck(areas.value, postcode.value); };
</script>

<style>
.s2-cover-card { max-width: 560px; }
.s2-cover-row { display: flex; gap: .5rem; }
.s2-cover-row .s2-input { flex: 1; text-transform: uppercase; }
.s2-cover-answer { min-height: 1.5rem; margin-top: .9rem; }
.s2-cover-msg { padding: .8rem 1rem; border-radius: var(--t-radius-sm); margin: 0; display: flex; flex-wrap: wrap; gap: .25rem .75rem; align-items: baseline; }
.s2-cover-msg[data-answer="yes"] { background: color-mix(in srgb, #1d9a4a 13%, var(--t-surface)); }
.s2-cover-msg[data-answer="near"] { background: color-mix(in srgb, #d9a400 16%, var(--t-surface)); }
.s2-cover-msg[data-answer="no"] { background: var(--t-surface-2); }
.s2-u { text-decoration: underline; font-weight: 600; }
</style>
